import { cardQueueUpdatedAt } from "@/lib/financialWork/feedClient";
import { isExpenseQueueCard } from "@/lib/financialWork/incomeRecordQueueDisplay";
import type { LiffFinancialWorkCard } from "@/lib/financialWork/types";

export type QueueSortColumn = "date" | "money_in" | "money_out" | "related";
export type QueueSortDir = "asc" | "desc";

function readContextMinor(card: LiffFinancialWorkCard, key: string): number {
  const raw = card.contextRows?.find((row) => row.key === key)?.value;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function queueRowDateMs(card: LiffFinancialWorkCard): number {
  const ms = Date.parse(cardQueueUpdatedAt(card));
  return Number.isFinite(ms) ? ms : 0;
}

function incomingPaidMinor(card: LiffFinancialWorkCard): number {
  const fromChild = (card.outstanding?.items ?? [])
    .map((row) => row.paidAmountMinor)
    .find((value) => value != null && value > 0);
  return fromChild ?? 0;
}

function matchedOutMinor(card: LiffFinancialWorkCard): number {
  const evidenceMatched = (card.evidenceItems ?? [])
    .map((item) => item.matchedAmountMinor)
    .filter((value): value is number => value != null);
  if (evidenceMatched.length > 0) {
    return evidenceMatched.reduce((sum, value) => sum + value, 0);
  }
  if (card.requirementStatus?.complete && card.amountSummary) {
    return card.amountSummary.primaryAmountMinor;
  }
  return 0;
}

function relatedMinor(card: LiffFinancialWorkCard): number {
  const remaining = card.outstanding?.remainingAmountMinor;
  if (remaining != null) return remaining;
  if (card.amountSummary?.basis === "remaining") {
    return card.amountSummary.primaryAmountMinor;
  }
  return card.amountSummary?.primaryAmountMinor ?? 0;
}

function isIncomeLike(card: LiffFinancialWorkCard): boolean {
  const workerId = card.availableActions.find((action) => action.dataNav?.workerId)
    ?.dataNav?.workerId;
  if (
    workerId === "bill-lin-income" ||
    workerId === "bill-lin-receivable" ||
    workerId === "bill-lin-receivable-slip"
  ) {
    return true;
  }
  if (workerId === "bill-lin-expense" || card.objectRef.kind === "expense") {
    return false;
  }
  const haystack = `${card.title} ${card.subtitle ?? ""} ${card.objectRef.kind}`;
  if (/ใบเสร็จ|รายจ่าย|เงินออก/.test(haystack)) return false;
  return /เงินเข้า|โอน|ผู้โอน|สลิป|invoice/i.test(haystack);
}

export function queueRowSortMetrics(
  card: LiffFinancialWorkCard,
  closedView: boolean,
) {
  if (closedView) {
    const transfer =
      readContextMinor(card, "transfer_amount") ||
      card.amountSummary?.primaryAmountMinor ||
      0;
    const allocated =
      readContextMinor(card, "allocated_amount") || (transfer > 0 ? transfer : 0);
    const expense = isExpenseQueueCard(card);
    return {
      dateMs: queueRowDateMs(card),
      moneyInMinor: expense ? 0 : transfer,
      moneyOutMinor: expense ? (allocated > 0 ? allocated : transfer) : 0,
      relatedMinor: transfer,
    };
  }

  const paid = incomingPaidMinor(card);
  const out = matchedOutMinor(card);
  const related = relatedMinor(card);
  const income = isIncomeLike(card);

  // Match table columns: เงินเข้า only when there is an actual payment; do not
  // treat remaining/related on open receivables as money-in.
  return {
    dateMs: queueRowDateMs(card),
    moneyInMinor: income ? paid : 0,
    moneyOutMinor: !income ? out : 0,
    relatedMinor: related,
  };
}

const METRIC_KEY: Record<
  QueueSortColumn,
  keyof ReturnType<typeof queueRowSortMetrics>
> = {
  date: "dateMs",
  money_in: "moneyInMinor",
  money_out: "moneyOutMinor",
  related: "relatedMinor",
};

export function sortWorkQueueCardsByColumn(
  cards: LiffFinancialWorkCard[],
  column: QueueSortColumn,
  dir: QueueSortDir,
  closedView: boolean,
): LiffFinancialWorkCard[] {
  const metric = METRIC_KEY[column];
  return [...cards].sort((left, right) => {
    const diff =
      queueRowSortMetrics(left, closedView)[metric] -
      queueRowSortMetrics(right, closedView)[metric];
    return dir === "asc" ? diff : -diff;
  });
}
